import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, Grid, MenuProps } from 'antd';
import { ListFilter, MoreHorizontal, RotateCw, Upload } from 'lucide-react';
import { TabItem } from '@/components/Tab/TabBar';
import { OrderStatusTab } from '@/types/columns';
import { useOrdersToolbarFilters } from '@/hooks/useOrdersToolbarFilters';
import { selectOpenExport, useExportModalStore } from '@/stores/useExportModalStore';
import usePermission from '@/hooks/usePermission';
import { PermissionName } from '@/types';
import OrdersViewTabs from './OrdersViewTabs';
import FilterSurface from './FilterSurface';
import FilterTokens from './FilterTokens';
import ActionRequiredButton from './ActionRequiredButton';
import ColumnsButton, { ColumnOption } from './ColumnsButton';
import { ACTION_REQUIRED } from '@/hooks/useOrdersToolbarFilters';
import { MenuField } from './FilterMenu';
import { FOCUS_RING, formatCount } from './toolbarConfig';

/**
 * Orders list header — ONE bar on top of the table: views on the start side,
 * result count + Filter + ⋯ on the end side. Applied filters show as a slim
 * row of editable tokens underneath, only when there are any.
 */
export interface OrdersToolbarProps {
  view: { tabs: TabItem[]; activeTab: OrderStatusTab; onTabChange: (tab: OrderStatusTab) => void };
  filter: {
    activeSubTab: string;
    onSubTabChange: (subTab: string) => void;
    actionRequiredCount?: number;
    /** On the Problematic tab: shows only Action required orders; again shows all. */
    onToggleActionRequired?: () => void;
    /** GET /orders/count for the active bucket, keyed by sub-status. */
    statusCounts?: Record<string, number>;
  };
  summary: { total?: number };
  /** Bulk resolve via CSV — passed only when it applies (Problematic + permission). */
  onBulkResolve?: () => void;
  /** Columns the merchant may hide on this tab. */
  columnOptions?: ColumnOption[];
}

const ghostButton = `inline-flex items-center justify-center gap-1.5 h-8 rounded-lg border-0 bg-transparent text-[13px] font-semibold text-sd-foreground hover:bg-sd-muted cursor-pointer shrink-0 ${FOCUS_RING}`;

type Open = { field: MenuField | null; from: 'button' | 'token' } | null;

const OrdersToolbar = ({
  view,
  filter,
  summary,
  onBulkResolve,
  columnOptions,
}: OrdersToolbarProps) => {
  const { t } = useTranslation();
  const openExport = useExportModalStore(selectOpenExport);
  const { hasPermission } = usePermission();
  const filters = useOrdersToolbarFilters(
    view.activeTab,
    filter.activeSubTab,
    filter.onSubTabChange,
    filter.statusCounts,
  );
  const [open, setOpen] = useState<Open>(null);

  const menuItems: MenuProps['items'] = [
    ...(onBulkResolve
      ? [
          {
            key: 'bulk-resolve',
            icon: <RotateCw className='w-4 h-4' />,
            label: String(t('bulk_resolve_csv', { defaultValue: 'Bulk resolve via CSV' })),
            onClick: onBulkResolve,
          },
        ]
      : []),
    {
      key: 'export',
      icon: <Upload className='w-4 h-4' />,
      label: String(t('export_this_list')),
      onClick: openExport,
      disabled: !hasPermission(PermissionName.ORDER_AND_SHIPMENT),
    },
  ];
  const count = filters.activeCount;
  // lg…xl: the views take row 1, so tokens share row 2 with Filter instead of adding a third row.
  const screens = Grid.useBreakpoint();
  const inline = !!screens.lg && !screens.xl;
  const tokens =
    count > 0 ? (
      <FilterTokens
        inline={inline}
        filters={filters}
        total={summary.total}
        openField={open?.from === 'token' ? open.field : null}
        onOpenField={(field) => setOpen(field ? { field, from: 'token' } : null)}
      />
    ) : null;

  return (
    <div data-testid='orders-toolbar' className='min-w-0 bg-sd-card'>
      <div className='flex flex-wrap items-center gap-x-3 gap-y-1 min-h-[52px] px-2 sm:px-3 py-2'>
        <div className='basis-full xl:basis-0 xl:flex-1 min-w-0'>
          <OrdersViewTabs
            tabs={view.tabs}
            activeTab={view.activeTab}
            onChange={(key) => view.onTabChange(key as OrderStatusTab)}
            alert={{ key: OrderStatusTab.ON_HOLD, count: filter.actionRequiredCount ?? 0 }}
          />
        </div>
        <div className='flex items-center gap-1 w-full xl:w-auto xl:ms-auto shrink-0'>
          {inline && tokens ? <div className='flex-1 min-w-0'>{tokens}</div> : null}
          {count === 0 && summary.total !== undefined ? (
            <span
              aria-live='polite'
              className='px-1.5 text-[12px] text-sd-muted-foreground tabular-nums whitespace-nowrap me-auto xl:me-0'
            >
              {String(t('orders_result_count', { n: formatCount(summary.total) }))}
            </span>
          ) : null}
          {/* Problematic only, as in the old filter row (owner 11-02). */}
          {filter.onToggleActionRequired && view.activeTab === OrderStatusTab.ON_HOLD ? (
            <ActionRequiredButton
              count={filter.actionRequiredCount ?? 0}
              active={
                view.activeTab === OrderStatusTab.ON_HOLD && filter.activeSubTab === ACTION_REQUIRED
              }
              onToggle={filter.onToggleActionRequired}
            />
          ) : null}
          <FilterSurface
            filters={filters}
            open={open?.from === 'button'}
            onOpenChange={(o) => setOpen(o ? { field: null, from: 'button' } : null)}
          >
            <button
              type='button'
              aria-haspopup='dialog'
              aria-expanded={open?.from === 'button'}
              aria-label={count ? `${String(t('filter'))} (${count})` : undefined}
              data-filter='button'
              className={`${ghostButton} px-2.5 ${count ? 'bg-sd-muted' : ''} ${count && !inline ? 'ms-auto xl:ms-0' : ''}`}
            >
              <ListFilter aria-hidden className='w-4 h-4' />
              {String(t('filter'))}
              {count ? (
                <span className='min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-sd-foreground text-white text-[11px] font-bold tabular-nums'>
                  {count}
                </span>
              ) : null}
            </button>
          </FilterSurface>
          {columnOptions?.length ? <ColumnsButton options={columnOptions} /> : null}
          <Dropdown trigger={['click']} placement='bottomRight' menu={{ items: menuItems }}>
            <button
              type='button'
              aria-label={String(t('more_actions'))}
              title={String(t('more_actions'))}
              className={`${ghostButton} w-8`}
            >
              <MoreHorizontal aria-hidden className='w-4 h-4' />
            </button>
          </Dropdown>
        </div>
      </div>
      {inline ? null : tokens}
    </div>
  );
};

export default OrdersToolbar;
